// Rated Orders › case modal — status, note, Super Admin Comment, Chat, Compensate.
// Shared by the queue page and the Complaint section of the order details modal.
// Writes go through setComplaintStatus / CompensateModal / SuperAdminComment; the caller
// receives a booking patch through onUpdated to refresh whatever it is showing.
import React, { useEffect, useState } from 'react';
import Select from 'react-select';
import { Badge, Button, Input, Label, Modal, ModalBody, ModalFooter, ModalHeader } from 'reactstrap';
import { createNotification } from '../../../../helpers/Utils';
import ChatWithCustomerButton from './ChatWithCustomerButton';
import CompensateModal from './CompensateModal';
import SuperAdminComment from './SuperAdminComment';
import { COMPLAINT_STATUS, COMPLAINT_STATUS_ORDER, OPEN_COMPLAINT_STATUSES, PILL, RESOLUTION_TYPES, SOFT_SELECT_STYLES, setComplaintStatus } from './complaint';

const statusOptions = COMPLAINT_STATUS_ORDER.map((value) => ({ value, label: COMPLAINT_STATUS[value].label }));

const CaseModal = ({ booking, user, isOpen, dismiss, onUpdated }) => {
  const current = booking?.complaint?.status || 'pending';
  const [form, setForm] = useState({ status: current, type: 'explained', note: '' });
  const [busy, setBusy] = useState(false);
  const [compensate, setCompensate] = useState(false);
  const id = booking?.key || booking?.id || booking?.bookingId;

  // Open on the CURRENT status; "Update case" stays disabled until it is changed.
  useEffect(() => {
    if (isOpen) setForm({ status: current, type: booking?.complaint?.resolutionType || 'explained', note: '' });
  }, [isOpen, id]); // eslint-disable-line react-hooks/exhaustive-deps

  const submit = async () => {
    setBusy(true);
    try {
      const extra = form.status === 'resolved' ? { resolutionType: form.type, note: form.note } : { note: form.note };
      const update = await setComplaintStatus({ booking, status: form.status, user, ...extra });
      const complaint = { ...(booking?.complaint || {}) };
      Object.entries(update).forEach(([k, v]) => { if (k.startsWith('complaint.')) complaint[k.slice('complaint.'.length)] = v; });
      createNotification('success', `Complaint marked ${COMPLAINT_STATUS[form.status].label}${update['complaint.apologySentAt'] ? ' · apology push sent to the customer' : ''}`, 'Rated Orders');
      onUpdated && onUpdated({ caseStatus: form.status, complaintOpen: OPEN_COMPLAINT_STATUSES.includes(form.status), complaint });
      dismiss();
    } catch (e) {
      createNotification('error', e?.message || 'Update failed', 'Rated Orders');
    } finally { setBusy(false); }
  };

  if (!booking) return null;
  const score = Number.isFinite(Number(booking.rate)) ? Number(booking.rate) : booking.complaint?.score;
  const phone = `${booking.country_code || ''}${booking.customer_number || ''}`.replace(/\D/g, '');

  return (
    <>
      <Modal isOpen={!!isOpen && !compensate} toggle={dismiss} backdrop="static">
        <ModalHeader toggle={dismiss}>Case · order #{booking.orderNumber}</ModalHeader>
        <ModalBody>
          <div className="mb-3 d-flex justify-content-between align-items-start" style={{ gap: '12px' }}>
            <div className="text-small text-muted">
              {booking.customer_name}{phone ? ` · +${phone}` : ''} · {booking.sp_name} · {score ?? '—'} / 5
              {booking.reason ? <><br />“{booking.reason}”</> : null}
            </div>
            <ChatWithCustomerButton booking={booking} size="xs" />
          </div>
          <Label>Status</Label>
          <Select
            options={statusOptions} classNamePrefix="select" isSearchable={false}
            value={statusOptions.find((o) => o.value === form.status)}
            onChange={(v) => setForm((f) => ({ ...f, status: v?.value || f.status }))}
            menuPlacement="auto" styles={SOFT_SELECT_STYLES}
            formatOptionLabel={(o) => (
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', minWidth: 0 }}>
                <Badge color={COMPLAINT_STATUS[o.value]?.color || 'secondary'} pill style={{ ...PILL, flex: 'none', width: '90px', textAlign: 'center' }}>{o.label}</Badge>
                <span className="text-muted text-small" style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{COMPLAINT_STATUS[o.value]?.hint}</span>
              </div>
            )}
          />
          {form.status === 'resolved' ? (
            <>
              <Label className="mt-3">Resolution</Label>
              <Select options={RESOLUTION_TYPES} classNamePrefix="select" styles={SOFT_SELECT_STYLES} menuPlacement="auto"
                value={RESOLUTION_TYPES.find((r) => r.value === form.type)}
                onChange={(v) => setForm((f) => ({ ...f, type: v?.value || 'other' }))} />
            </>
          ) : null}
          <Label className="mt-3">Note{form.status === 'resolved' ? ' (what was done)' : ' (optional)'}</Label>
          <Input type="textarea" rows={3} value={form.note} onChange={(e) => setForm((f) => ({ ...f, note: e.target.value }))}
            placeholder="e.g. Apologised by chat, 20 AED compensation, provider warned about timing" />
          <div className="border-top mt-4 pt-3">
            <SuperAdminComment booking={booking} user={user} title="Super Admin Comment (on the order)"
              onSaved={(text) => onUpdated && onUpdated({ superAdminComment: text })} />
          </div>
        </ModalBody>
        <ModalFooter className="justify-content-between">
          <Button color="success" outline onClick={() => setCompensate(true)}>
            <i className="iconsminds-gift-box mr-1" />{booking.complaint?.compensation ? 'Compensate again…' : 'Compensate…'}
          </Button>
          <div>
            <Button color="secondary" outline className="mr-2" onClick={dismiss} disabled={busy}>Cancel</Button>
            <Button color="primary" disabled={busy || form.status === current} onClick={submit}>{busy ? 'Saving…' : 'Update case'}</Button>
          </div>
        </ModalFooter>
      </Modal>
      {compensate ? (
        <CompensateModal
          booking={booking} user={user} isOpen={compensate}
          dismiss={() => setCompensate(false)}
          onIssued={(compensation) => {
            const total = Number(booking.complaint?.compensationTotal || 0) + Number(compensation.amount);
            onUpdated && onUpdated({ complaint: { ...(booking.complaint || {}), compensation, compensationTotal: total } });
          }}
        />
      ) : null}
    </>
  );
};

export default CaseModal;
